import { useEffect } from "react";
import { useLocation } from "@tanstack/react-router";

function collectOverflowingElements() {
  if (typeof window === "undefined" || typeof document === "undefined") return [] as HTMLElement[];

  const viewportWidth = document.documentElement.clientWidth;
  const all = Array.from(document.querySelectorAll<HTMLElement>("body *"));

  return all.filter((el) => {
    const style = window.getComputedStyle(el);
    if (style.display === "none" || style.position === "fixed") return false;

    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;

    return rect.right > viewportWidth + 1 || rect.left < -1;
  });
}

function describeElement(el: HTMLElement) {
  const cls = el.className && typeof el.className === "string" ? `.${el.className.trim().split(/\s+/).join(".")}` : "";
  const id = el.id ? `#${el.id}` : "";
  return `${el.tagName.toLowerCase()}${id}${cls}`;
}

export function useOverflowDebug(scope = "board-admin") {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;

    const enabled = new URLSearchParams(window.location.search).get("debugOverflow") === "1";
    if (!enabled) return;

    const run = () => {
      const root = document.documentElement;
      const body = document.body;
      const offenders = collectOverflowingElements();

      console.group(`[overflow-debug:${scope}]`);
      console.log({
        viewport: root.clientWidth,
        rootScrollWidth: root.scrollWidth,
        bodyScrollWidth: body.scrollWidth,
        offenders: offenders.length,
      });

      offenders.slice(0, 25).forEach((el, idx) => {
        const rect = el.getBoundingClientRect();
        el.style.outline = "1px dashed #ef4444";
        console.log(`${idx + 1}. ${describeElement(el)}`, {
          left: Math.round(rect.left),
          right: Math.round(rect.right),
          width: Math.round(rect.width),
          overflowRight: Math.round(rect.right - root.clientWidth),
        });
      });

      console.groupEnd();
    };

    const raf = requestAnimationFrame(run);
    window.addEventListener("resize", run);
    window.addEventListener("orientationchange", run);
    const timeout = window.setTimeout(run, 120);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timeout);
      window.removeEventListener("resize", run);
      window.removeEventListener("orientationchange", run);
    };
  }, [scope, location.pathname, location.search]);
}
